/* eslint-disable no-console, no-undef */
import React, { useCallback, useRef, useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Card, Tag, Spin, Switch, notification } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { usePostMonitoringCommandMutation } from "../../../redux/monitoring/monitoringAPI";
import { updateSendCommand } from "../../../redux/monitoring/monitoringSlice";
// FLAT BUFFERS
import {
  decodeFrontEndMessage,
  computeDebounceKey,
  isFaultMessage,
} from "../../../flatbuffers/decoders/frontEndMessageDecoder";
import {
  applyDeltaToState,
  createDefaultState,
  DisplayMapMode,
} from "../../../flatbuffers/decoders/applyDeltaToState";
import { createUiCmdPayload } from "../../../flatbuffers/builders/uiCmdBuilder";
import { FaultMessageT } from "../../../flatbuffers/generated/fault/fault-message";
// MONITORING STATE
import { useBankDispatch } from "../socketMessageState/BankMessagesContext";
// SOCKET EMITTER
import socketEmitter from "./MonitoringWebSocketEmitter";
// CHANGE DETECTION
import hasStateChanged from "./hasStateChanged";
// AUTHORIZATION
import GETJwtTokenSocket from "../../../redux/authentication/authenticationSocket";
import GETJwtToken from "../../../redux/authentication/authentication";
// CUSTOM HOOKS
import useGetAllKeys from "../../../customHooks/GET_requests/useGetAllKeys";
import useGetDebounceOverrides from "../../../customHooks/GET_requests/useGetDebounceOverrides";
import useGetBankProtocols from "../../../customHooks/GET_requests/useGetBankProtocols";
import resolveDebounceTime from "../../../utils/debounceLookup";
import {
  SentWebSocketCommandSummary,
  showWebSocketCommandSentNotification,
} from "./WebSocketCommandSentAlert";
// INTERFACES
import { store, RootState } from "../../../app/store";
import { LayoutInterface, SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { OneHotMessageContent } from "../../../flatbuffers/generated/ln2/one-hot-message-content";
import { uuidToString } from "../../../utils/uuid";
import {
  compoundKey,
  bankKey,
  faultKey,
  faultKeyPrefix,
  BankKey,
  CompoundKey,
  FaultKey,
} from "../../../utils/typedKeys";

const FAULT_PERSISTENCE_KEY_PREFIX = "liftnet_expired_faults_";

// Map FaultContent to Messages API device_type for debounce lookup
const faultContentToDeviceType = (fc: FaultContent): string => {
  switch (fc) {
    case FaultContent.ElevatorFaultMessage:
      return "ELEVATOR";
    case FaultContent.EscalatorFaultMessage:
      return "ESCALATOR";
    default:
      return "";
  }
};

// LocalStorage helpers for fault persistence (stores FaultKey strings)
const getPersistedFaults = (bankIdStr: string): Set<FaultKey> => {
  const storageKey = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankIdStr}`;
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return new Set(JSON.parse(stored) as FaultKey[]);
    }
  } catch (e) {
    console.error("❌ [FAULT_TIMER] Failed to load persisted faults:", e);
  }
  return new Set();
};

const savePersistedFaults = (bankIdStr: string, faults: Set<FaultKey>) => {
  const storageKey = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankIdStr}`;
  try {
    if (faults.size > 0) {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(faults)));
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch (e) {
    console.error("❌ [FAULT_TIMER] Failed to save persisted faults:", e);
  }
};

function MonitoringWebSocketConnection({
  serverId,
  serverName,
  channelType,
  liftName,
  liftId,
  channelId,
  layouts,
}: {
  serverId: string;
  serverName: string;
  channelType: string;
  liftName: string;
  liftId: string;
  channelId: string;
  layouts: LayoutInterface[];
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const socket = useRef<WebSocket | null>(null);
  const SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_EDGE_SERVER;
  const myIntervalCheckSocketConnection: any = useRef(null);
  const CONNECTING = 0;
  const OPEN = 1;
  const CLOSING = 2;
  const CLOSED = 3;
  const dispatch = useBankDispatch();
  const dispatchRedux = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [enabledAutoRefresh, setEnabledAutoRefresh] = useState<boolean>(true);
  const [enabledSendPing, setEnabledSendPing] = useState<boolean>(true);
  const [hasRawWsMessage, setHasRawWsMessage] = useState<boolean>(false);

  // ************************************************ */
  // FAULT TIMER STATE ****************************** */
  const activeFaults = useRef<Map<FaultKey, ReturnType<typeof setTimeout>>>(new Map());
  const expiredFaults = useRef<Set<FaultKey>>(new Set());
  const persistedFaultsByBank = useRef<Map<string, Set<FaultKey>>>(new Map());
  const messageDebounceMap = useRef<Map<string, number>>(new Map());
  const debounceOverrideMap = useRef<Map<string, number>>(new Map());
  const prevOverrideFingerprint = useRef<string>("");
  const lastMessagesByLift = useRef<Map<string, SocketMessageInterface>>(new Map());
  const liftStateRef = useRef<Map<CompoundKey, SocketMessageInterface>>(new Map());
  const pendingDispatches = useRef<Map<CompoundKey, SocketMessageInterface>>(new Map());
  const deltaDebounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const rafHandle = useRef<number | null>(null);
  const displayMapModes = useRef<Map<BankKey, DisplayMapMode>>(new Map());
  const protocolByBank = useRef<Map<string, string>>(new Map());
  const prevDebounceFingerprint = useRef<string>("");
  const lastRawWsBuffer = useRef<ArrayBuffer | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const showQueueInfo = useSelector((state: RootState) => state.monitoring.showQueueInfo);
  const stopSocketConnection = useSelector(
    (state: RootState) => state.monitoring.stopSocketConnection,
  );
  const sendCommand = useSelector((state: RootState) => state.monitoring.sendCommand);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [postCommand, { isLoading: isLoadingPostCommand }] = usePostMonitoringCommandMutation();
  const { keys } = useGetAllKeys();

  // Derive stable bank IDs from layouts for debounce override fetching
  const bankIds = React.useMemo(() => layouts.map((l) => l.idBank), [layouts]);
  const { debounceOverrides } = useGetDebounceOverrides(bankIds);
  // Resolve per-bank adapter protocol type (e.g. "mce") from /banks/:id, since
  // the monitor-view layout payload does not include the full adapter object.
  const { protocolByBank: bankProtocols } = useGetBankProtocols(bankIds);

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const flushPendingDispatches = useCallback(() => {
    if (store.getState().monitoring.openReplayPanel) {
      pendingDispatches.current.clear();
      rafHandle.current = null;
      return;
    }
    pendingDispatches.current.forEach((payload) => {
      dispatch({
        type: "UPDATE_MESSAGE",
        payload,
      });
    });
    pendingDispatches.current.clear();
    rafHandle.current = null;
  }, [dispatch]);

  const scheduleDispatch = useCallback(
    (stateKey: CompoundKey, payloadData: any) => {
      pendingDispatches.current.set(stateKey, payloadData);
      if (rafHandle.current === null) {
        rafHandle.current = requestAnimationFrame(flushPendingDispatches);
      }
    },
    [flushPendingDispatches],
  );

  const getDebounceTime = useCallback(
    (
      faultContent: FaultContent,
      code: number,
      targetBankId: string,
      targetLiftId: string,
    ): number => {
      const deviceType = faultContentToDeviceType(faultContent);
      return resolveDebounceTime(
        debounceOverrideMap.current,
        messageDebounceMap.current,
        targetBankId,
        targetLiftId,
        deviceType,
        code,
      );
    },
    [],
  );

  const shouldFaultBlink = useCallback(
    (activeFaultList: FaultMessageT[], bankIdStr: string, targetLiftId: string): boolean => {
      const persistedFaults = persistedFaultsByBank.current.get(bankIdStr) || new Set();
      const prefix = faultKeyPrefix(bankIdStr, targetLiftId);
      const allExpiredFaults = Array.from(expiredFaults.current);
      const hasExpiredFaultsForThisLift = allExpiredFaults.some((key) => key.startsWith(prefix));
      const matchesPersistedFault = activeFaultList.some((f) =>
        persistedFaults.has(faultKey(bankIdStr, targetLiftId, f.faultType, f.fault?.message ?? 0)),
      );

      return hasExpiredFaultsForThisLift || matchesPersistedFault;
    },
    [],
  );

  const manageFaultTimers = useCallback(
    (
      faultsWithDebounce: Array<{
        fault: FaultMessageT;
        debounceMs: number;
      }>,
      bankIdStr: string,
      targetLiftId: string,
      isMce: boolean,
    ) => {
      const incomingFaultKeys = new Set(
        faultsWithDebounce.map((f) =>
          faultKey(bankIdStr, targetLiftId, f.fault.faultType, f.fault.fault?.message ?? 0),
        ),
      );
      const currentFaults = activeFaults.current;
      const prefix = faultKeyPrefix(bankIdStr, targetLiftId);

      const triggerImmediateUpdate = () => {
        const key = faultKeyPrefix(bankIdStr, targetLiftId);
        const lastMsg = lastMessagesByLift.current.get(key);
        if (lastMsg) {
          const shouldBlink = shouldFaultBlink(lastMsg.activeFaults, bankIdStr, targetLiftId);
          if (!store.getState().monitoring.openReplayPanel) {
            dispatch({
              type: "UPDATE_MESSAGE",
              payload: { ...lastMsg, faults: shouldBlink },
            });
          }
        }
      };

      const updatePersistence = (fk: FaultKey, shouldAdd: boolean) => {
        try {
          const currentSet = persistedFaultsByBank.current.get(bankIdStr) || new Set<FaultKey>();
          if (shouldAdd) currentSet.add(fk);
          else currentSet.delete(fk);
          persistedFaultsByBank.current.set(bankIdStr, currentSet);
          savePersistedFaults(bankIdStr, currentSet);
        } catch (e) {
          console.error("Error updating fault persistence:", e);
        }
      };

      // 1. START TIMERS — faults with a configured debounce time blink after
      // the delay elapses. For MCE banks, faults without a debounce time
      // (<= 0) blink immediately, since a zero debounce means "no delay".
      // For non-MCE banks, faults without a debounce time are ignored.
      faultsWithDebounce.forEach(({ fault, debounceMs }) => {
        const fk = faultKey(bankIdStr, targetLiftId, fault.faultType, fault.fault?.message ?? 0);
        if (debounceMs <= 0) {
          if (isMce && !expiredFaults.current.has(fk)) {
            expiredFaults.current.add(fk);
            updatePersistence(fk, true);
          }
          return;
        }
        if (!currentFaults.has(fk) && !expiredFaults.current.has(fk)) {
          const timer = setTimeout(() => {
            expiredFaults.current.add(fk);
            currentFaults.delete(fk);
            updatePersistence(fk, true);
            triggerImmediateUpdate();
          }, debounceMs);
          currentFaults.set(fk, timer);
        }
      });

      // 2. CANCEL TIMERS
      Array.from(currentFaults.keys()).forEach((fk) => {
        if (fk.startsWith(prefix)) {
          if (!incomingFaultKeys.has(fk)) {
            const timer = currentFaults.get(fk);
            if (timer) clearTimeout(timer);
            currentFaults.delete(fk);
            updatePersistence(fk, false);
          }
        }
      });

      // 3. STOP BLINKING
      Array.from(expiredFaults.current).forEach((fk) => {
        if (fk.startsWith(prefix)) {
          if (!incomingFaultKeys.has(fk)) {
            expiredFaults.current.delete(fk);
            updatePersistence(fk, false);
            triggerImmediateUpdate();
          }
        }
      });
    },
    [dispatch, shouldFaultBlink],
  );

  const onMessageFlatBuffer = useCallback(
    async (socketMessage: ArrayBuffer | Blob | string) => {
      // Skip all processing when replay panel is open
      if (store.getState().monitoring.openReplayPanel) {
        return;
      }
      if (typeof socketMessage === "string") {
        console.error("❌ WS sent STRING instead of binary:", socketMessage);
        return;
      }
      let buffer: ArrayBuffer;
      if (socketMessage instanceof Blob) {
        buffer = await socketMessage.arrayBuffer();
      } else {
        buffer = socketMessage;
      }

      lastRawWsBuffer.current = buffer.slice(0);
      setHasRawWsMessage(true);

      const bytes = new Uint8Array(buffer);
      try {
        const fem = decodeFrontEndMessage(bytes);
        if (!fem) return;

        const bankIdStr = uuidToString(fem.bankId!);
        const liftIdStr = uuidToString(fem.liftId!);
        const stateKey = compoundKey(fem.bankId!, fem.liftId!);
        const bk = bankKey(fem.bankId!);

        // Look up or create default state for this lift
        const existingState =
          liftStateRef.current.get(stateKey) || createDefaultState(fem.bankId!, fem.liftId!);

        // Apply the delta to get merged state
        const mergedState = applyDeltaToState(existingState, fem, displayMapModes.current.get(bk));

        // Run fault timer logic with the merged activeFaults
        const { activeFaults: activeFaultList } = mergedState;
        const faultsWithDebounce = activeFaultList.map((fault) => {
          const debounceMs = getDebounceTime(
            fault.faultType,
            fault.fault?.message ?? 0,
            bankIdStr,
            liftIdStr,
          );
          return { fault, debounceMs };
        });
        const isMce = protocolByBank.current.get(bankIdStr) === "mce";
        manageFaultTimers(faultsWithDebounce, bankIdStr, liftIdStr, isMce);

        const shouldBlink = shouldFaultBlink(activeFaultList, bankIdStr, liftIdStr);

        const isSocketClosed = socket.current?.readyState === WebSocket.CLOSED;

        const payloadData: SocketMessageInterface = {
          ...mergedState,
          faults: shouldBlink,
          connected: mergedState.connected && !isSocketClosed,
        };

        // Save merged state for future delta accumulation
        liftStateRef.current.set(stateKey, mergedState);

        // Save to ref for timer-triggered updates
        const liftFaultPrefix = faultKeyPrefix(bankIdStr, liftIdStr);
        const prevPayload = lastMessagesByLift.current.get(liftFaultPrefix);
        lastMessagesByLift.current.set(liftFaultPrefix, payloadData);

        // Only schedule dispatch if something meaningful changed
        if (hasStateChanged(prevPayload, payloadData)) {
          try {
            const schemaFrontEndMessageJson = JSON.stringify(
              fem,
              (_key, value) => (typeof value === "bigint" ? value.toString() : value),
              2,
            );
            const socketMessageLiftJson = JSON.stringify(
              payloadData,
              (_key, value) => (typeof value === "bigint" ? value.toString() : value),
              2,
            );
            socketEmitter.emit("preDispatchMessage", {
              timestamp: Date.now(),
              bankId: bankIdStr,
              liftId: liftIdStr,
              payloadJson: schemaFrontEndMessageJson,
              schemaFrontEndMessageJson,
              socketMessageLiftJson,
            });
          } catch (jsonError) {
            console.error(
              "[WS][DECODED] Failed to stringify front_end_message payload:",
              jsonError,
            );
          }

          const encodedMessage = fem.msg?.msg;
          const isServiceMessage =
            encodedMessage?.contentType === EncodedMessageContent.OneHotMessage &&
            encodedMessage?.content &&
            (encodedMessage.content as { messageType?: number }).messageType ===
              OneHotMessageContent.Service_ServiceMessage;

          if (isServiceMessage) {
            dispatch({
              type: "UPDATE_MESSAGE",
              payload: payloadData,
            });
            return;
          }

          // Check debounce time for non-fault deltas (faults have their own timer system)
          const debounceKey = computeDebounceKey(fem);
          const isFault = isFaultMessage(fem);
          let debounceMs = 0;
          if (!isFault && debounceKey) {
            // Try 3-tier override lookup first, fall back to default map
            const parts = debounceKey.split(":");
            if (parts.length === 2) {
              debounceMs = resolveDebounceTime(
                debounceOverrideMap.current,
                messageDebounceMap.current,
                bankIdStr,
                liftIdStr,
                parts[0],
                Number(parts[1]),
              );
            } else {
              debounceMs = messageDebounceMap.current.get(debounceKey) ?? 0;
            }
          }
          if (debounceMs > 0) {
            // Cancel any existing debounce timer for this delta key on this lift
            const timerKey = `${stateKey}:${debounceKey}`;
            const existing = deltaDebounceTimers.current.get(timerKey);
            if (existing) clearTimeout(existing);

            deltaDebounceTimers.current.set(
              timerKey,
              setTimeout(() => {
                deltaDebounceTimers.current.delete(timerKey);
                // Re-read the latest state since it may have changed during debounce
                const latest = lastMessagesByLift.current.get(liftFaultPrefix);
                if (latest) {
                  scheduleDispatch(stateKey, latest);
                }
              }, debounceMs),
            );
          } else {
            scheduleDispatch(stateKey, payloadData);
          }
        }
      } catch (err) {
        console.error("❌ Decoder FAILED", err);
      }
    },
    [manageFaultTimers, shouldFaultBlink, getDebounceTime, scheduleDispatch],
  );

  const onDownloadRawWsMessage = useCallback(() => {
    if (!lastRawWsBuffer.current) return;
    const blob = new Blob([lastRawWsBuffer.current], {
      type: "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "msg.bin";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const refreshAll = useCallback(async () => {
    const token = await GETJwtToken();
    const BODY = {
      token,
      body: {
        lift_name: liftName,
        lift_id: liftId,
        channel_id: "",
        key_id: "",
        target_value: 0,
      },
      serverId,
    };
    postCommand(BODY);
  }, [liftName, liftId, serverId, postCommand]);

  // Keep stable refs for callbacks that change identity frequently.
  // The socket event handlers read through these refs so the socket
  // lifecycle useEffect never re-runs due to callback identity changes.
  const onMessageRef = useRef(onMessageFlatBuffer);
  onMessageRef.current = onMessageFlatBuffer;

  const onSocketOpenRef = useRef(() => {
    if (channelType !== "ADAPTER") refreshAll();
  });
  useEffect(() => {
    onSocketOpenRef.current = () => {
      if (channelType !== "ADAPTER") refreshAll();
    };
  }, [channelType, refreshAll]);

  const onConnect = useCallback(async () => {
    if (socket.current?.readyState !== WebSocket.OPEN) {
      const token = await GETJwtTokenSocket();
      socket.current = new WebSocket(`${SOCKET_URL}?serverID=${serverId}&access_token=${token}`);
      socket.current.addEventListener("open", () => {
        onSocketOpenRef.current();
      });
      socket.current.addEventListener("close", () => {});
      socket.current.addEventListener("message", (event: any) => {
        onMessageRef.current(event.data);
      });
    }
  }, [serverId, SOCKET_URL]);

  const onDisconnect = useCallback(() => {
    socket.current?.close();
  }, []);

  // Use a ref for enabledAutoRefresh so the interval callback is always current
  const enabledAutoRefreshRef = useRef(enabledAutoRefresh);
  enabledAutoRefreshRef.current = enabledAutoRefresh;

  const onConnectRef = useRef(onConnect);
  onConnectRef.current = onConnect;

  useEffect(() => {
    if (keys && keys.length > 0) {
      const map = new Map<string, number>();
      keys.forEach((key: any) => {
        const msg = key.message;
        if (msg && msg.enum_id !== null && msg.enum_id !== undefined && key.debounce_time) {
          map.set(`${msg.device_type}:${msg.enum_id}`, key.debounce_time);
        }
      });

      // Skip if the debounce map hasn't actually changed
      const fingerprint = JSON.stringify(Array.from(map.entries()).sort());
      if (fingerprint === prevDebounceFingerprint.current) return;
      prevDebounceFingerprint.current = fingerprint;

      messageDebounceMap.current = map;

      // Reset active timers so faults re-evaluate with correct debounce values.
      activeFaults.current.forEach((timer) => clearTimeout(timer));
      activeFaults.current.clear();

      // Clear race-condition faults (added with debounce=0 before map loaded)
      // but restore any persisted faults from localStorage so they keep blinking.
      expiredFaults.current.clear();
      persistedFaultsByBank.current.forEach((faultSet) => {
        faultSet.forEach((fk) => expiredFaults.current.add(fk));
      });

      dispatch({ type: "UPDATE_DEBOUNCE_MAP", payload: map });
    }
  }, [keys]);

  // Build per-bank/per-lift debounce override map
  useEffect(() => {
    if (!debounceOverrides || debounceOverrides.length === 0) return;

    const map = new Map<string, number>();
    debounceOverrides.forEach((override: any) => {
      const msg = override.message;
      if (!msg || msg.enum_id === null || msg.enum_id === undefined) return;

      const suffix = `${msg.device_type}:${msg.enum_id}`;
      if (override.lift_id) {
        // Lift-specific override
        map.set(`${override.bank_id}:${override.lift_id}:${suffix}`, override.debounce_time);
      } else {
        // Bank-wide override
        map.set(`${override.bank_id}:*:${suffix}`, override.debounce_time);
      }
    });

    const fingerprint = JSON.stringify(Array.from(map.entries()).sort());
    if (fingerprint === prevOverrideFingerprint.current) return;
    prevOverrideFingerprint.current = fingerprint;

    debounceOverrideMap.current = map;

    // Reset active timers so faults re-evaluate with override values.
    activeFaults.current.forEach((timer) => clearTimeout(timer));
    activeFaults.current.clear();

    expiredFaults.current.clear();
    persistedFaultsByBank.current.forEach((faultSet) => {
      faultSet.forEach((fk) => expiredFaults.current.add(fk));
    });
  }, [debounceOverrides]);

  // Load persisted faults and display map modes whenever layouts change
  useEffect(() => {
    layouts.forEach((layout) => {
      const persisted = getPersistedFaults(layout.idBank);
      if (persisted.size > 0) {
        persistedFaultsByBank.current.set(layout.idBank, persisted);
        // Pre-populate expiredFaults so blinking starts immediately on refresh
        persisted.forEach((fk) => expiredFaults.current.add(fk));
      }
      // Cache per-bank display map mode from adapter config
      const mode = (layout as any).adapter?.socket_message_priority?.displayMapMode ?? "accumulate";
      displayMapModes.current.set(layout.idBank as BankKey, mode);
    });
  }, [layouts]);

  // Cache per-bank adapter protocol type (e.g. "mce") resolved from /banks/:id.
  // The monitor-view layout payload does not include the adapter object, so we
  // rely on the dedicated protocol lookup hook here.
  useEffect(() => {
    bankProtocols.forEach((protocol, idBank) => {
      protocolByBank.current.set(idBank, protocol);
    });
  }, [bankProtocols]);

  // Socket connection lifecycle -- only depends on serverId.
  // All callbacks are accessed through stable refs so that identity changes
  // in useCallback chains never cause socket teardown + reconnect, which
  // would discard the LiftCloud hashmap replay (serviceMode, etc.).
  useEffect(() => {
    if (serverId) {
      lastRawWsBuffer.current = null;
      setHasRawWsMessage(false);
      onConnectRef.current();
      myIntervalCheckSocketConnection.current = setInterval(() => {
        if (socket.current?.readyState === WebSocket.CLOSED && enabledAutoRefreshRef.current) {
          onConnectRef.current();
        }
      }, 30 * 1000);
    }
    return () => {
      socketEmitter.removeAllListeners("sendCommand");
      socket.current?.close();
      if (myIntervalCheckSocketConnection.current)
        clearInterval(myIntervalCheckSocketConnection.current);
      activeFaults.current.forEach((timer) => clearTimeout(timer));
      activeFaults.current.clear();
      expiredFaults.current.clear();
      liftStateRef.current.clear();
      if (rafHandle.current !== null) {
        cancelAnimationFrame(rafHandle.current);
        rafHandle.current = null;
      }
      pendingDispatches.current.clear();
    };
  }, [serverId]);

  useEffect(() => {
    if (stopSocketConnection) socket.current?.close();
    else onConnectRef.current();
  }, [stopSocketConnection]);

  // Send command effect (from development)
  useEffect(() => {
    if (!sendCommand.sendCommand) return;

    const { command, position, active, bankId, liftId: targetLiftId } = sendCommand.body || {};

    if (
      command === undefined ||
      position === undefined ||
      active === undefined ||
      bankId === undefined ||
      targetLiftId === undefined
    ) {
      notification.warning({
        message: "Command not sent",
        description: "Invalid command payload.",
        placement: "topRight",
        duration: 4,
      });
      dispatchRedux(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
      return;
    }

    const commandSummary = {
      command,
      position,
      active,
      channelId,
      bankId,
      liftId: targetLiftId,
    } as SentWebSocketCommandSummary;

    if (socket.current?.readyState === WebSocket.OPEN) {
      const payload = createUiCmdPayload({
        message: command,
        position,
        active,
        channelId: commandSummary.channelId,
        bankId,
        liftId: targetLiftId,
      });
      socket.current.send(payload);
      showWebSocketCommandSentNotification(commandSummary);
    } else {
      notification.warning({
        message: "Command not sent",
        description: "WebSocket is disconnected. Reconnect and try again.",
        placement: "topRight",
        duration: 4,
      });
    }

    dispatchRedux(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
  }, [sendCommand, channelId, dispatchRedux]);

  return (
    <Spin spinning={isLoadingPostCommand}>
      {showQueueInfo && (
        <div>
          <Card
            title={serverName}
            style={{ width: 550, marginLeft: "15px", marginTop: "10px" }}
            extra={
              <div>
                {socket.current?.readyState !== OPEN && (
                  <Button onClick={onConnect}>Connect</Button>
                )}
                {socket.current?.readyState === OPEN && (
                  <Button onClick={onDisconnect}>Disconnect</Button>
                )}
                <Switch
                  onChange={setEnabledAutoRefresh}
                  checked={enabledAutoRefresh}
                  checkedChildren="Reconnection"
                  unCheckedChildren="Reconnection"
                  style={{ marginLeft: "10px" }}
                />
                <Switch
                  onChange={setEnabledSendPing}
                  checked={enabledSendPing}
                  checkedChildren="Ping"
                  unCheckedChildren="Ping"
                  style={{ marginLeft: "10px" }}
                />
                <Button
                  onClick={onDownloadRawWsMessage}
                  disabled={!hasRawWsMessage}
                  style={{ marginLeft: "10px" }}
                >
                  Download msg.bin
                </Button>
              </div>
            }
          >
            {/* Summary Info */}
            <p style={{ marginBottom: "-1px" }}>
              <b
                className={
                  socket.current?.readyState === OPEN
                    ? "generalStyles__info"
                    : "generalStyles__warning"
                }
              >
                Status:
              </b>{" "}
              <b style={{ color: "white" }}>
                {socket.current?.readyState === OPEN && <Tag color="green">OPEN</Tag>}
                {socket.current?.readyState === CONNECTING && <Tag color="cyan">CONNECTING</Tag>}
                {socket.current?.readyState === CLOSING && <Tag color="orange">CLOSING</Tag>}
                {socket.current?.readyState === CLOSED && <Tag color="red">CLOSED</Tag>}
              </b>
            </p>
            <p style={{ marginBottom: "-1px" }}>
              <b>Socket Connection:</b> <span>EDGE (ONLINE)</span>
            </p>
            <p style={{ marginBottom: "-1px" }}>
              <b>Server:</b> <span>{serverId}</span>
            </p>
            <p style={{ marginBottom: "-1px" }}>
              <b>Channel Type:</b> <span>{channelType}</span>
            </p>
          </Card>
        </div>
      )}
    </Spin>
  );
}

export default MonitoringWebSocketConnection;
